<template>
  <ContentWrapperControl>
    <div class="flex flex-col gap-4">
      <div
        :class="[
          'border rounded-lg px-4 py-3 flex items-center justify-between mt-3',
          cardClasses.bg,
          cardClasses.border,
        ]"
      >
        <div class="flex flex-col">
          <span class="font-bold text-sm">
            {{ this.lang("lbl_0000491") }}
          </span>
        </div>

        <div class="flex flex-col items-end gap-2">
          <span :class="['font-bold leading-none text-sm', cardClasses.text]">
            ₱ {{ formatAmount(currentBalance) }}
          </span>

          <span class="mt-1 text-xxs">
            As of {{ formatShortDate(currentAsOf) }}
          </span>
        </div>
      </div>

      <div v-if="showSummaryToggle" class="flex justify-center mt-3">
        <div
          class="flex flex-row rounded-full bg-grey-lighter border border-grey-light text-xs"
        >
          <div
            :class="[
              'flex justify-center items-center px-5 py-2 rounded-full cursor-pointer transition',
              !show_summary
                ? 'bg-white font-semibold text-grey-darkest'
                : 'text-grey-dark',
            ]"
            @click="show_summary = false"
          >
            All
          </div>
          <div
            :class="[
              'flex justify-center items-center px-5 py-2 rounded-full cursor-pointer transition',
              show_summary
                ? 'bg-white font-semibold text-grey-darkest'
                : 'text-grey-dark',
            ]"
            @click="show_summary = true"
          >
            Summary
          </div>
        </div>
      </div>

      <div class="bg-white border rounded-lg p-4 mt-3">
        <div class="flex items-center justify-between w-full mb-3">
          <div class="flex flex-col">
            <span class="font-bold text-sm">
              {{ this.lang("lbl_0000488") }}
            </span>
            <span class="text-xxs text-grey-dark mt-1">
              {{
                this.timestamp
                  ? `Last Retrieved: ${formatDateTime(this.timestamp)}`
                  : ""
              }}
            </span>
          </div>
          <img
            src="@/assets/icons/papsi_info.svg"
            class="w-icon h-icon"
            @click="show_description = true"
          />
        </div>

        <div
          class="overflow-hidden rounded-lg border border-grey-light bg-white"
        >
          <div
            class="flex items-center px-4 py-3 text-sm font-bold text-grey-darkest border-b border-grey-light box-border bg-grey-lighter"
          >
            <div class="w-24 shrink-0 text-center uppercase">
              {{ this.lang("lbl_0000489") }}
            </div>

            <div class="flex-1 text-center uppercase">
              {{
                showSummaryToggle && show_summary
                  ? this.lang("lbl_0000492")
                  : this.lang("lbl_0000493")
              }}
            </div>

            <div class="w-24 shrink-0 text-center uppercase">
              {{ this.lang("lbl_0000490") }}
            </div>
          </div>

          <div class="overflow-y-auto" style="max-height: 44vh">
            <div
              v-for="(item, index) in displayedTransactions"
              :key="index"
              class="flex items-center px-4 py-3 text-sm border-b border-[#E5E7EB] box-border"
              :class="index % 2 === 0 ? 'bg-white' : 'bg-grey-lighter'"
              @click="handleTransactionClick(item)"
            >
              <div class="w-24 shrink-0 text-center font-medium">
                {{ formatDate(item.date) }}
              </div>

              <div class="flex-1 text-center px-3 overflow-hidden">
                <div class="truncate font-medium">
                  {{
                    showSummaryToggle && show_summary
                      ? item.loan_id
                      : item.description
                  }}
                </div>
              </div>

              <div
                class="w-24 shrink-0 text-right font-bold"
                :class="showSummaryToggle && show_summary ? '' : getTransactionAmountClass(item)"
              >
                {{
                  showSummaryToggle && show_summary
                    ? getSummaryAmountText(item)
                    : getTransactionAmountText(item)
                }}
              </div>
            </div>

            <div
              v-if="displayedTransactions.length === 0"
              class="bg-white py-10 text-center text-sm text-gray-400"
            >
              {{ this.lang("lbl_0000506") }}
            </div>
          </div>
        </div>

        <div v-if="hasFilterButton" class="flex justify-center mt-4">
          <ButtonControl :theme="buttonTheme" @click="show_filter = true">
            Filter by Date
          </ButtonControl>
        </div>
        <div class="bg-info-light rounded-lg shadow p-3 mt-4">
          <div class="flex items-center">
            <img
              src="@/assets/icons/papsi_info_blue.svg"
              class="w-icon h-icon mr-2"
            />
            <div class="text-xs text-grey-dark italic">
              <span class="mr-1">
                {{ lang("lbl_0000504") }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ContentWrapperControl>
  <ModalControl
    :show="show_description"
    :title="selected_name"
    :dismiss="true"
    @closed="show_description = false"
  >
    <div class="text-sm text-justify p-2">
      {{ getAccountDescription(selected_code) }}
    </div>
  </ModalControl>
  <ModalControl
    :show="show_grouped"
    :title="`${!isLoan ? lang('lbl_0000498') : lang('lbl_0000494')}${
      selected_id ? ` - ${selected_id}` : ''
    }`"
    :dismiss="true"
    @closed="show_grouped = false"
  >
    <div class="flex flex-col gap-3">
      <div class="flex flex-row text-sm justify-between">
        <!-- <span class="font-bold text-primary">
          {{ selected_id }}
        </span> -->
        <div class="flex flex-row justify-between items-center w-full text-sm">
          <template v-if="isLoan">
            <span>
              {{ this.lang("lbl_0000495") }}:
              <span class="font-bold">
                {{ selected_tracking }}
              </span>
            </span>

            <span>
              {{ this.lang("lbl_0000496") }}:
              <span class="font-bold">
                {{ formatAmount(selected_balance) }}
              </span>
            </span>
          </template>

          <template v-else-if="selected_code === 'SSD'">
            <span>
              {{ this.lang("lbl_0000497") }}:
              <span class="font-bold">
                {{ formatDate(selected_term) }}
              </span>
            </span>

            <span>
              {{ this.lang("lbl_0000496") }}:
              <span class="font-bold">
                {{ formatAmount(selected_balance) }}
              </span>
            </span>
          </template>
        </div>
      </div>
      <!-- <div class="flex flex-row text-sm justify-between mt-4">
        <span>
          Tracking:
          <span class="font-bold">
            {{ selected_tracking }}
          </span>
        </span>

        <span>
          Term:
          <span class="font-bold">
            {{ selected_term }}
          </span>
        </span>
      </div> -->

      <div class="border rounded-lg overflow-hidden mt-4">
        <div class="flex bg-grey-lighter font-bold text-xs px-3 py-2 border-b">
          <div class="w-24 text-center">DATE</div>
          <div class="flex-1 text-center">DESCRIPTION</div>
          <div class="w-24 text-right">AMOUNT</div>
        </div>

        <div
          v-for="(item, index) in selected_transactions"
          :key="index"
          class="flex px-3 py-2 text-xs border-b"
          :class="index % 2 === 0 ? 'bg-white' : 'bg-grey-lighter'"
        >
          <div class="w-24 text-center">
            {{ formatDate(item.date) }}
          </div>

          <div class="flex-1 text-center truncate px-2">
            {{ item.description }}
          </div>

          <div
            class="w-24 text-right font-bold"
            :class="getTransactionAmountClass(item)"
          >
            {{ getTransactionAmountText(item) }}
          </div>
        </div>
      </div>
    </div>
  </ModalControl>
  <ModalControl
    :show="show_filter"
    title="Filter Transactions"
    :dismiss="true"
    @closed="show_filter = false"
  >
    <div class="flex flex-col">
      <div class="mt-2 mb-2">
        <label class="text-sm font-semibold">From</label>

        <input
          type="date"
          v-model="filter_from"
          :max="today"
          class="w-full border rounded-lg p-2"
        />
      </div>

      <div class="mt-2 mb-2">
        <label class="text-sm font-semibold">To</label>
        <input
          type="date"
          v-model="filter_to"
          :min="filter_from || null"
          :max="today"
          class="w-full border rounded-lg p-2"
        />
      </div>

      <div class="flex flex-row justify-end mt-4">
        <ButtonControl
          :outline="true"
          @click="
            filter_from = '';
            filter_to = '';
          "
          class="mr-2"
        >
          {{ this.lang("lbl_0000017") }}
        </ButtonControl>

        <ButtonControl
          :disabled="!filter_from || !filter_to"
          @click="show_filter = false"
        >
          {{ this.lang("lbl_0000505") }}
        </ButtonControl>
      </div>
    </div>
  </ModalControl>
</template>

<script>
import ContentWrapperControl from "@/components/controls/ContentWrapperControl.vue";
import ButtonControl from "@/components/controls/ButtonControl.vue";
import ModalControl from "@/components/controls/ModalControl.vue";
import Balance from "@/services/Balance";
import mixin from "@/mixin";

export default {
  mixins: [mixin],

  emits: [
    "success",
    "error",
    "alert",
    "login",
    "logout",
    "showloader",
    "hideloader",
    "showblinder",
    "hideblinder",
    "terminate",
    "checkauth",
    "nofooter",
    "shownavbar",
    "hidenavbar",
    "opensidebar",
    "closed",
    "agreed",
  ],

  props: {
    code: {
      type: String,
      default: "SAV",
    },
  },

  components: {
    ContentWrapperControl,
    ButtonControl,
    ModalControl,
  },

  watch: {
    filter_from(val) {
      if (this.filter_to && new Date(this.filter_to) < new Date(val)) {
        this.filter_to = val;
      }
    },
  },

  data() {
    return {
      transactions: [],
      grouped_transactions: [],
      selected_transactions: [],
      visible_count: 10,
      timestamp: "",
      selected_id: "",
      selected_code: "",
      selected_name: "",
      selected_balance: "",
      show_summary: false,
      show_description: false,
      show_grouped: false,
      selected_tracking: "",
      selected_term: "",
      show_filter: false,
      filter_from: "",
      filter_to: "",
    };
  },

  computed: {
    currentBalance() {
      if (!this.transactions.length) return 0;
      return this.transactions[0].balance || 0;
    },

    currentAsOf() {
      if (!this.transactions.length) return 0;
      return this.transactions[0].date || 0;
    },

    cardClasses() {
      if (this.selected_code === "SHR") {
        return {
          bg: "bg-audit-light",
          border: "border-audit",
          text: "text-audit",
        };
      }

      if (this.isLoan) {
        return {
          bg: "bg-secondary-light",
          border: "border-secondary-dark",
          text: "text-secondary-dark",
        };
      }

      return {
        bg: "bg-primary-light",
        border: "border-primary",
        text: "text-primary",
      };
    },

    buttonTheme() {
      if (this.selected_code === "SHR") {
        return "audit";
      }

      return this.isLoan ? "secondary_dark" : "success";
    },

    // displayedTransactions() {
    //   if (this.isLoan && this.show_summary) {
    //     return this.grouped_transactions.slice(0, this.visible_count);
    //   }

    //   if (this.isLoan && !this.show_summary) {
    //     const grouped = {};

    //     for (const item of this.transactions) {
    //       const key = item.reference || item.description;

    //       if (!grouped[key]) {
    //         grouped[key] = {
    //           ...item,
    //           debit: 0,
    //           credit: 0,
    //         };
    //       }

    //       grouped[key].debit += parseFloat(item.debit || 0);
    //       grouped[key].credit += parseFloat(item.credit || 0);
    //     }

    //     return Object.values(grouped).slice(0, this.visible_count);
    //   }

    //   return this.transactions.slice(0, this.visible_count);
    // },

    // hasMoreTransactions() {
    //   return this.visible_count < this.transactions.length;
    // },

    isLoan() {
      const ledger = this.$store.state.ledgers.types.find(
        (item) => item.code === this.code,
      );

      return ledger?.is_loan === true || ledger?.is_loan === "True";
    },

    showSummaryToggle() {
      return this.isLoan || this.selected_code === "SSD";
    },

    today() {
      return new Date().toISOString().split("T")[0];
    },

    filteredTransactions() {
      let list = [];

      if ((this.isLoan || this.selected_code === "SSD") && this.show_summary) {
        list = this.grouped_transactions;
      } else if (this.isLoan || this.selected_code === "SSD") {
        const grouped = {};
        const group_by_reference = this.selected_code === "SSD";

        for (const item of this.transactions) {
          const key = group_by_reference
            ? (item.reference_id || "").trim() || "__EMPTY_REFERENCE_ID__"
            : item.reference || item.description;

          if (!grouped[key]) {
            grouped[key] = {
              ...item,
              debit: 0,
              credit: 0,
            };
          }

          grouped[key].debit += parseFloat(item.debit || 0);
          grouped[key].credit += parseFloat(item.credit || 0);
        }

        list = Object.values(grouped);
      } else {
        list = this.transactions;
      }

      if (!this.filter_from && !this.filter_to) return list;

      return list.filter((item) => {
        const date = new Date(item.date);

        if (this.filter_from) {
          const from = new Date(this.filter_from);
          from.setHours(0, 0, 0, 0);

          if (date < from) return false;
        }

        if (this.filter_to) {
          const to = new Date(this.filter_to);
          to.setHours(23, 59, 59, 999);

          if (date > to) return false;
        }

        return true;
      });
    },

    displayedTransactions() {
      const list = this.filteredTransactions;

      if (!this.filter_from && !this.filter_to) {
        return list.slice(0, 10);
      }

      return list;
    },

    hasFilterButton() {
      let total =
        this.isLoan && this.show_summary
          ? this.grouped_transactions.length
          : this.isLoan
          ? this.filteredTransactions.length
          : this.transactions.length;

      return total > 10;
    },
  },

  mounted() {
    this.$emit("showloader");
    this.$emit("checkauth", true);
    this.$emit("nofooter", true);

    let state = this.$store.state;

    this.selected_code = this.code;

    const ledger = state.ledgers.types.find(
      (item) => item.code === this.selected_code,
    );

    this.selected_name = ledger ? ledger.name : this.selected_code;

    this.$emit("shownavbar", {
      title: `${this.selected_name}`,
      show: true,
      back: true,
      refresh: true,
    });

    this.getTransactionHistory(
      state.hash,
      this.selected_code,
      state.uuid,
      state.key,
    );
  },

  methods: {
    getTransactionHistory(hash, type, uuid, key) {
      const cached = this.$store.state.ledgers.historical[type];

      if (cached) {
        this.transactions = cached.transactions;
        this.timestamp = cached.timestamp;

        if (this.isLoan || this.selected_code === "SSD") {
          this.groupTransactions();
        }

        this.$emit("hideloader");
        return;
      }
      Balance.createTransactionRequest(hash, type, uuid, key)
        .then((response) => {
          const transactions = (response.data.data || []).reverse();

          this.transactions = transactions;
          this.timestamp = response.data.timestamp || "";

          this.$store.state.ledgers.historical[type] = {
            transactions,
            timestamp: this.timestamp,
          };

          if (this.isLoan || this.selected_code === "SSD") {
            this.groupTransactions();
          }

          this.$emit("hideloader");
        })
        .catch((error) => {
          this.$emit("error", error);
          this.$router.push("/dashboard");
        });
    },

    formatAmount(value) {
      return parseFloat(value || 0).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getSummaryAmountText(item) {
      return this.formatAmount(item.balance || 0);
    },

    getSummaryAmountClass(item) {
      const balance = parseFloat(item.balance || 0);
      const isPositive = balance > 0;

      if (this.isLoan) {
        return isPositive ? "text-error" : "text-primary";
      }

      return isPositive ? "text-primary" : "text-error";
    },

    getTransactionAmountText(item) {
      if (this.isLoan) {
        const debit = parseFloat(item.debit || 0);

        if (debit > 0) {
          return this.formatAmount(debit);
        }

        return `(${this.formatAmount(parseFloat(item.credit || 0))})`;
      }

      const credit = parseFloat(item.credit || 0);

      if (credit > 0) {
        return this.formatAmount(credit);
      }

      return `(${this.formatAmount(parseFloat(item.debit || 0))})`;
    },

    getTransactionAmountClass(item) {
      const isPositive = this.isLoan
        ? parseFloat(item.debit || 0) > 0
        : parseFloat(item.credit || 0) > 0;

      return isPositive ? "text-primary" : "text-error";
    },

    formatDate(date) {
      if (!date) return "";

      return new Date(date).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    },

    groupTransactions() {
      const map = {};

      for (const t of this.transactions) {
        let key;

        if (this.selected_code === "SSD") {
          key = (t.reference_id || "").trim();

          if (!key) key = "__EMPTY_REFERENCE_ID__";
        } else {
          key = t.loan_id;
        }

        if (!key) continue;

        if (!map[key]) map[key] = [];
        map[key].push(t);
      }

      this.grouped_transactions = Object.entries(map)
        .map(([key, items]) => {
          const sorted = [...items].sort(
            (a, b) => new Date(a.date) - new Date(b.date),
          );

          const earliest = sorted[0];
          const latest = sorted[sorted.length - 1];

          const begin = items.find(
            (x) => x.description?.toLowerCase() === "begin",
          );

          const grossAmount = parseFloat(begin?.credit || 0);
          const totalDebit = items.reduce(
            (sum, item) => sum + parseFloat(item.debit || 0),
            0,
          );
          const totalCredit = items.reduce(
            (sum, item) => sum + parseFloat(item.credit || 0),
            0,
          );
          const currentBalance = totalDebit - totalCredit;

          const totalTerms =
            parseInt(
              String(begin?.term || earliest?.term || "").replace(/[^\d]/g, ""),
              10,
            ) || 0;

          const paymentsMade = items.filter((x) => {
            const description = (x.description || "").trim().toLowerCase();

            return (
              description !== "begin" &&
              description !== "loan" &&
              (parseFloat(x.debit || 0) > 0 || parseFloat(x.credit || 0) > 0)
            );
          }).length;

          const tracking = `${paymentsMade} of ${totalTerms}`;

          const maturity_dates = items
            .map((x) => x.mature_date)
            .filter(
              (d) =>
                d &&
                d !== "0000-00-00" &&
                d !== "null" &&
                !Number.isNaN(new Date(d).getTime()),
            )
            .sort((a, b) => new Date(b) - new Date(a));

          return {
            loan_id:
              this.selected_code === "SSD"
                ? key === "__EMPTY_REFERENCE_ID__"
                  ? ""
                  : key
                : key,

            reference_id: key === "__EMPTY_REFERENCE_ID__" ? "" : key,
            reference: latest.reference,
            description: latest.description,
            date: earliest.date,
            balance: currentBalance,
            mature_date: maturity_dates[0] || "",
            term: begin?.term || earliest.term,
            gross_amount: grossAmount,
            tracking,
          };
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    handleTransactionClick(item) {
      if ((this.isLoan || this.selected_code === "SSD") && this.show_summary) {
        // this.selected_id = item.loan_id;
        this.selected_id =
          this.selected_code === "SSD"
            ? (item.reference_id || "").trim()
            : item.loan_id;
        this.selected_balance = item.balance;

        if (this.isLoan) {
          this.selected_tracking = item.tracking;
          this.selected_term = item.term;
        } else {
          this.selected_tracking = "";
          this.selected_term = item.mature_date;
        }

        // this.selected_transactions = this.transactions
        //   .filter((t) => t.loan_id === item.loan_id)
        //   .sort((a, b) => new Date(b.date) - new Date(a.date));

        const key =
          this.selected_code === "SSD"
            ? item.reference_id || "__EMPTY_REFERENCE_ID__"
            : item.loan_id;

        this.selected_transactions = this.transactions
          .filter((t) => {
            if (this.selected_code === "SSD") {
              const reference_id =
                (t.reference_id || "").trim() || "__EMPTY_REFERENCE_ID__";
              return reference_id === key;
            }

            return t.loan_id === key;
          })
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        this.show_grouped = true;
      }
    },
  },
};
</script>

<style scoped>
button:active {
  transform: scale(0.98);
}
</style>
